// Core
import React from 'react';

// Libraries
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Hooks
import { useBreakpoint } from '../../../../../../../../hooks/useBreakpoint';

// Plugins
import {
  capitalizeFirstLetter,
  DASHBOARD_NULLISH_TEXT,
} from '../../../../../../../../plugins/general';
import {
  booleanToQuantityStatus,
  booleanToQuantityText,
  DEVICE_COMMUNICATION_THRESHOLD,
  isEquipmentRunning,
} from '../../../../../../../../plugins/crusher';

// Types
import { Theme } from '../../../../../../../../types/theme';

// Components
import { Col } from '../../../../../../../../layout/elements/Col';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../../../layout/elements/Paragraph';
import { QuantityCard } from '../../../../../../../../layout/elements/QuantityCard';
import { Row } from '../../../../../../../../layout/elements/Row';

// Component
import { InformationProps } from './Information.types';
import {
  ModelBackgroundContainer,
  ModelContainer,
  ModelContentWrapper,
  ModelTitle,
  QuantityCardContainer,
  QuantityContainer,
  QuantityContent,
  QuantityTitle,
} from './Information.style';

function Information({
  className,
  unitData,
  edgeStatus,
  testId,
}: InformationProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();

  // Determine if the equipment is running
  const equipmentRunning: boolean = isEquipmentRunning(
    edgeStatus,
    unitData?.timestamp,
    DEVICE_COMMUNICATION_THRESHOLD,
  );

  return (
    <div className={className} data-testid={testId}>
      <Row style={{ rowGap: 16 }}>
        <Col cols={24} xl={6}>
          <ModelContainer>
            <ModelTitle>
              <Paragraph size={isXs ? ParagraphSize.xs : ParagraphSize.sm}>
                {t('view.crusher.maintenance.setup.information.modelTitle')}
              </Paragraph>
            </ModelTitle>
            <ModelBackgroundContainer running={equipmentRunning}>
              <ModelContentWrapper>
                <Paragraph
                  color={
                    equipmentRunning
                      ? theme.color.crusherDashboard.maintenance.setup.information.modelTextColor
                      : theme.color.crusherDashboard.maintenance.setup.information.modelTextColorOff
                  }
                  size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  weight={ParagraphWeight.bold}
                >
                  {capitalizeFirstLetter(
                    unitData?.crusherModelPLC
                      ? unitData?.crusherModelPLC
                      : DASHBOARD_NULLISH_TEXT,
                  )}
                </Paragraph>
              </ModelContentWrapper>
            </ModelBackgroundContainer>
          </ModelContainer>
        </Col>
        <Col cols={24} xl={18}>
          <QuantityContainer>
            <QuantityTitle>
              <Paragraph size={isXs ? ParagraphSize.xs : ParagraphSize.sm}>
                {t('view.crusher.maintenance.setup.information.quantityTitle')}
              </Paragraph>
            </QuantityTitle>
            <QuantityContent>
              <Row style={{ rowGap: 16 }}>
                <Col cols={24} xl={8}>
                  <QuantityCardContainer>
                    <QuantityCard
                      description={
                        t('view.crusher.maintenance.setup.information.firstCardDescription')
                      }
                      status={booleanToQuantityStatus(
                        unitData?.crusherMotorQty,
                        edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                        unitData?.timestamp ? unitData?.timestamp : null,
                      )}
                      quantity={booleanToQuantityText(unitData?.crusherMotorQty)}
                    />
                  </QuantityCardContainer>
                </Col>
                <Col cols={24} xl={8}>
                  <QuantityCardContainer>
                    <QuantityCard
                      description={
                        t('view.crusher.maintenance.setup.information.secondCardDescription')
                      }
                      status={booleanToQuantityStatus(
                        unitData?.lubePumpQty,
                        edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                        unitData?.timestamp ? unitData?.timestamp : null,
                      )}
                      quantity={booleanToQuantityText(unitData?.lubePumpQty)}
                    />
                  </QuantityCardContainer>
                </Col>
                <Col cols={24} xl={8}>
                  <QuantityCardContainer>
                    <QuantityCard
                      description={
                        t('view.crusher.maintenance.setup.information.thirdCardDescription')
                      }
                      status={booleanToQuantityStatus(
                        unitData?.tankHeaterQty,
                        edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                        unitData?.timestamp ? unitData?.timestamp : null,
                      )}
                      quantity={booleanToQuantityText(unitData?.tankHeaterQty)}
                    />
                  </QuantityCardContainer>
                </Col>
              </Row>
            </QuantityContent>
          </QuantityContainer>
        </Col>
      </Row>
    </div>
  );
}

export { Information };
